/* eslint-disable max-len */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Button from '../Button';
import { DropdownSimple } from '../Dropdown';
import Icon from '../Icon';
import CalendarIcon from '../../svg/icons/ic_calendar-solid_20x20.svg';
import PriorityNotify from './PriorityNotify';
import {
  CONSTANTS,
  getPriorityNotifyExperiment,
  getPriorityNotifyState,
  getNotifyCopy,
  getNotifyDetails,
  getNotifyTimeDisplay,
  getChangeOrConfirmCta,
  getNotifyStaticLabels,
  NOOP,
} from './notifyUtils';

const NotifyPreState = (props) => {
  const priorityNotifyExperiment = useSelector(getPriorityNotifyExperiment);
  const {
    experimentKey,
    variationKey,
    variables: experimentVariables,
    isActive: isPriorityExperimentActive,
  } = priorityNotifyExperiment;
  const {
    activeNotifications,
    isGda,
    venueName,
    eventName,
    notifyStart,
    notifyEnd,
    notifyTimeOptions,
    onClickCancelNotify,
    onClickSetNotify,
    selectEnd,
    selectStart,
    selectedDate,
    selectedQuantity,
    serviceTypeId,
  } = props;
  const { BUTTON_ID_CANCEL } = CONSTANTS;

  const isExistingNotify = activeNotifications && !!activeNotifications[serviceTypeId];
  const [isPriorityNotify, setIsPriorityNotify] = useState(
    activeNotifications?.[serviceTypeId]?.priorityNotify || false
  );

  const {
    shouldShowPriorityToggle,
    requiresMemberTimeSelection,
    isPriorityNotifyAutoEnabled,
    priorityNotifyEnabled,
  } = getPriorityNotifyState({
    isGda,
    isPriorityExperimentActive,
    experimentVariables,
    isExistingNotify,
    isPriorityNotify,
  });

  const { notifyHeader, notifyBody, notifyChangeOrConfirm } = getNotifyCopy({
    isGda,
    isPriorityExperimentActive,
    isExistingNotify,
    isPriorityNotify,
    experimentVariables,
  });

  const {
    isEvent, detailsLabel, detailsName, iconComponent, iconName, notifyQuantity,
  } = getNotifyDetails({
    activeNotifications, venueName, eventName, selectedQuantity,
  });

  const isRequiredTimeCreate = requiresMemberTimeSelection && !isExistingNotify;
  const [selectedNotifyStart, setSelectedNotifyStart] = useState(
    isRequiredTimeCreate ? '' : notifyStart
  );
  const [selectedNotifyEnd, setSelectedNotifyEnd] = useState(
    isRequiredTimeCreate ? '' : notifyEnd
  );

  const {
    displayNotifyStart, displayNotifyEnd, timeOptions, isConfirmDisabled,
  } = getNotifyTimeDisplay({
    requiresMemberTimeSelection,
    isRequiredTimeCreate,
    notifyStart,
    notifyEnd,
    selectedNotifyStart,
    selectedNotifyEnd,
    notifyTimeOptions,
  });

  const { changeOrConfirmVariant, changeOrConfirmTestId } = getChangeOrConfirmCta({
    isGda,
    isPriorityExperimentActive,
    isPriorityNotify,
    isPriorityNotifyAutoEnabled,
    isExistingNotify,
  });

  const {
    startEndLabel, toLabel, preferredStart, preferredEnd, notifyRemove, calendarIcon, partySizeIcon,
  } = getNotifyStaticLabels();

  return (
    <div data-testid="NotifyPreState">
      <div className="Notify__head">
        <h2>{notifyHeader}</h2>
        <p>{notifyBody}</p>
      </div>
      <div className="Notify__venue-details">
        {isEvent && <div className="Notify__event-label">{detailsLabel}</div>}
        <div className="Notify__venue-name">{detailsName}</div>
        <div className="Notify__details-row">
          <Icon component={CalendarIcon} name="calendar" role="img" ariaLabel={calendarIcon} />
          {moment(`${selectedDate}`).format('ddd, ll')}
        </div>
        <div className="Notify__details-row">
          <Icon component={iconComponent} name={iconName} role="img" ariaLabel={partySizeIcon} />
          <span>{notifyQuantity}</span>
        </div>
      </div>
      {shouldShowPriorityToggle && (
        <PriorityNotify
          isPriorityNotify={isPriorityNotify}
          setIsPriorityNotify={setIsPriorityNotify}
          priorityNotifyVariables={experimentVariables}
        />
      )}
      {(notifyStart || notifyEnd || requiresMemberTimeSelection) && (
        <div className="Notify__body">
          <div className="Notify__prompt">{startEndLabel}</div>
          <div className="Notify__select-times">
            <DropdownSimple
              name="start-time"
              options={timeOptions}
              onChange={(event) => {
                setSelectedNotifyStart(event.currentTarget.value);
                selectStart(event);
              }}
              value={displayNotifyStart}
              ariaLabel={preferredStart}
            />
            <span className="Notify__to">{toLabel}</span>
            <DropdownSimple
              name="end-time"
              options={timeOptions}
              onChange={(event) => {
                setSelectedNotifyEnd(event.currentTarget.value);
                selectEnd(event);
              }}
              value={displayNotifyEnd}
              ariaLabel={preferredEnd}
            />
          </div>
        </div>
      )}
      <div className="Notify__cta">
        {isExistingNotify && (
        <>
          <Button
            className="Notify__cancel"
            onClick={onClickCancelNotify}
            data-test-id={BUTTON_ID_CANCEL}
            lg
            variant="cancel"
          >
            {notifyRemove}
          </Button>
        </>
        )}
        <Button
          className="Notify__confirm"
          variant={changeOrConfirmVariant}
          onClick={(event) => onClickSetNotify(
            event,
            isPriorityExperimentActive ? {
              experimentKey,
              variationKey,
              priorityNotifyEnabled,
            } : undefined
          )}
          data-test-id={changeOrConfirmTestId}
          lg
          disabled={isConfirmDisabled}
        >
          {notifyChangeOrConfirm}
        </Button>
      </div>
    </div>
  );
};

NotifyPreState.propTypes = {
  activeNotifications: PropTypes.shape({
    [PropTypes.number]: PropTypes.shape({
      start: PropTypes.string,
      end: PropTypes.string,
    }),
  }),
  isGda: PropTypes.bool,
  venueName: PropTypes.string,
  eventName: PropTypes.string,
  notifyStart: PropTypes.string,
  notifyEnd: PropTypes.string,
  notifyTimeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  onClickCancelNotify: PropTypes.func,
  onClickSetNotify: PropTypes.func,
  selectEnd: PropTypes.func,
  selectStart: PropTypes.func,
  selectedDate: PropTypes.instanceOf(Date),
  selectedQuantity: PropTypes.number,
  serviceTypeId: PropTypes.number,
};
NotifyPreState.defaultProps = {
  activeNotifications: undefined,
  isGda: undefined,
  venueName: undefined,
  eventName: undefined,
  notifyStart: undefined,
  notifyEnd: undefined,
  notifyTimeOptions: [],
  onClickCancelNotify: NOOP,
  onClickSetNotify: NOOP,
  selectEnd: NOOP,
  selectStart: NOOP,
  selectedDate: undefined,
  selectedQuantity: undefined,
  serviceTypeId: undefined,
};
export default NotifyPreState;
